import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/base/ScrollToTop";
import NovelCard from "@/components/feature/NovelCard";
import {
  OrganizationSchema,
  WebSiteSchema,
  WebPageSchema,
  FAQPageSchema,
  BreadcrumbListSchema,
} from "@/components/base/StructuredData";
import {
  novelInfo,
  chapters,
  categoryList,
  categoryNovels,
  characters,
  reviews,
  faqList,
} from "@/mocks/novel";

export default function Home() {
  const allNovels = Object.values(categoryNovels).flat();

  return (
    <>
      <ScrollToTop />
      <OrganizationSchema />
      <WebSiteSchema />
      <WebPageSchema
        title="二枪入一洞 - 苏晓月小说完整版在线阅读 | 书香阁"
        description="《二枪入一洞》苏晓月著，情节跌宕、人物鲜活。提供完整剧情介绍、章节目录、人物关系梳理及读者热评。"
        url="https://shuxiangge.com/"
        datePublished="2025-10-15"
        dateModified="2026-08-07"
      />
      <BreadcrumbListSchema
        items={[{ name: "首页", url: "https://shuxiangge.com/" }]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Ancient%20Chinese%20martial%20arts%20fantasy%20landscape%20with%20misty%20mountains%20and%20ancient%20temples%2C%20dramatic%20atmospheric%20lighting%2C%20ink%20wash%20painting%20style%2C%20epic%20cinematic%20composition%2C%20warm%20golden%20and%20dark%20tones%2C%20mysterious%20and%20powerful%20aesthetic&width=1800&height=900&seq=hero-bg-home&orientation=landscape"
              alt="二枪入一洞 苏晓月 小说"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
            <div className="absolute inset-0 flex items-center justify-center px-4">
              <div className="text-center w-full max-w-3xl">
                <p className="text-accent-300 text-sm md:text-base font-medium tracking-widest mb-3">
                  苏晓月 · 武侠仙侠巨作
                </p>
                <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-4 leading-tight">
                  二枪入一洞
                </h1>
                <p className="text-foreground-200 text-sm md:text-lg max-w-2xl mx-auto leading-relaxed mb-8">
                  少年林逸偶得神兵「双龙」，踏上武道巅峰之路。江湖风云变幻，身世之谜层层揭开。
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Link
                    to="/article/1"
                    className="px-8 py-3 text-base font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-all whitespace-nowrap"
                  >
                    <i className="ri-book-read-line mr-2"></i>开始阅读
                  </Link>
                  <Link
                    to="#chapters"
                    className="px-8 py-3 text-base font-medium bg-background-50/20 text-background-50 backdrop-blur-sm rounded-full hover:bg-background-50/30 transition-all whitespace-nowrap border border-background-50/30"
                  >
                    <i className="ri-list-check mr-2"></i>章节目录
                  </Link>
                </div>
                <div className="flex items-center justify-center gap-6 mt-8 text-background-50/80 text-sm">
                  <span><strong className="text-background-50">{novelInfo.rating}</strong> 评分</span>
                  <span className="w-px h-4 bg-background-50/30"></span>
                  <span><strong className="text-background-50">{novelInfo.totalChapters}</strong> 章</span>
                  <span className="w-px h-4 bg-background-50/30"></span>
                  <span><strong className="text-background-50">{novelInfo.totalWords}</strong></span>
                  <span className="w-px h-4 bg-background-50/30"></span>
                  <span className="text-accent-300 font-medium">{novelInfo.status}</span>
                </div>
              </div>
            </div>
          </section>

          {/* Stats Bar */}
          <section className="bg-background-50 border-b border-background-200/70 py-6">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
                {[
                  { label: "累计评分", value: `${novelInfo.rating}分` },
                  { label: "收藏人数", value: `${(novelInfo.favorites / 10000).toFixed(1)}万` },
                  { label: "总字数", value: novelInfo.totalWords },
                  { label: "更新频率", value: novelInfo.updateFrequency },
                ].map((stat) => (
                  <div key={stat.label} className="text-center">
                    <p className="font-heading text-xl md:text-2xl font-bold text-primary-500">
                      {stat.value}
                    </p>
                    <p className="text-xs md:text-sm text-foreground-500 mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Synopsis */}
          <section className="py-14 md:py-20 bg-background-100">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-5xl mx-auto">
                <div className="flex flex-col lg:flex-row gap-10 items-start">
                  <div className="w-full lg:w-72 shrink-0">
                    <div className="aspect-[2/3] rounded-lg overflow-hidden bg-background-200 max-w-xs mx-auto lg:max-w-none">
                      <img
                        src={novelInfo.coverImage}
                        alt="二枪入一洞 苏晓月"
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="flex flex-wrap gap-2 mt-4 justify-center lg:justify-start">
                      {novelInfo.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-3 py-1 text-xs rounded-full bg-accent-100 text-accent-700 font-medium"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex-1">
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-1">
                      作品简介
                    </h2>
                    <p className="text-foreground-500 text-sm mb-6">
                      作者：{novelInfo.author} | 分类：{novelInfo.category} | 更新至：{novelInfo.lastUpdate}
                    </p>
                    <div className="space-y-4">
                      <p className="text-foreground-700 leading-relaxed text-sm md:text-base whitespace-pre-line">
                        {novelInfo.synopsis}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Categories */}
          <section className="py-14 md:py-20 bg-background-50">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-7xl mx-auto">
                <div className="text-center mb-10">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                    小说分类
                  </h2>
                  <p className="text-foreground-500 text-sm mt-3">
                    海量精品小说，总有一款适合你
                  </p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {categoryList.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="group flex flex-col items-center gap-3 p-6 rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-200 hover:bg-background-50 transition-all duration-300"
                    >
                      <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300">
                        <i className={`${cat.icon} text-xl`}></i>
                      </div>
                      <span className="font-heading text-sm font-semibold text-foreground-800 group-hover:text-primary-500 transition-colors">
                        {cat.name}
                      </span>
                      <span className="text-xs text-foreground-400">
                        {cat.count.toLocaleString()} 部
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Chapters */}
          <section id="chapters" className="py-14 md:py-20 bg-background-100">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-4xl mx-auto">
                <div className="text-center mb-10">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                    章节目录
                  </h2>
                  <p className="text-foreground-500 text-sm mt-3">
                    {novelInfo.title} · 共 {novelInfo.totalChapters} 章
                  </p>
                </div>
                <div className="space-y-2">
                  {chapters.map((ch) => (
                    <Link
                      key={ch.id}
                      to={`/article/${ch.id}`}
                      className="flex items-center justify-between p-4 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all group"
                    >
                      <div className="flex items-center gap-4">
                        <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 text-sm font-semibold group-hover:bg-primary-500 group-hover:text-background-50 transition-all shrink-0">
                          {ch.id}
                        </span>
                        <div>
                          <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors">
                            {ch.title}
                          </h3>
                          <p className="text-xs text-foreground-400 mt-0.5">
                            {ch.volume}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-foreground-400 shrink-0">
                        <span>{ch.wordCount} 字</span>
                        <span>{ch.publishDate}</span>
                      </div>
                    </Link>
                  ))}
                </div>
                <div className="text-center mt-8">
                  <Link
                    to="/category/wuxia"
                    className="inline-flex items-center gap-1 px-6 py-3 text-sm font-medium text-primary-500 bg-primary-50 rounded-full hover:bg-primary-100 transition-colors"
                  >
                    查看全部章节 <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* Characters */}
          <section className="py-14 md:py-20 bg-background-50">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-6xl mx-auto">
                <div className="text-center mb-10">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                    人物关系
                  </h2>
                  <p className="text-foreground-500 text-sm mt-3">
                    主要角色介绍与经典语录
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {characters.map((char, idx) => (
                    <div
                      key={idx}
                      className="p-6 rounded-lg bg-background-100 border border-background-200/70 hover:border-accent-200 transition-all"
                    >
                      <div className="w-14 h-14 flex items-center justify-center rounded-full bg-accent-100 text-accent-500 mb-4 mx-auto">
                        <i className="ri-user-3-line text-2xl"></i>
                      </div>
                      <h3 className="font-heading text-base font-semibold text-foreground-900 text-center">
                        {char.name}
                      </h3>
                      <p className="text-xs text-accent-600 text-center mt-1 font-medium">
                        {char.role}
                      </p>
                      <p className="text-sm text-foreground-600 mt-3 leading-relaxed">
                        {char.description}
                      </p>
                      <div className="mt-4 pt-3 border-t border-background-200/70">
                        <p className="text-xs text-foreground-400 italic">
                          「{char.quote}」
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Hot Novels */}
          <section className="py-14 md:py-20 bg-background-100">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-7xl mx-auto">
                <div className="flex items-center justify-between mb-10">
                  <div>
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                      热门推荐
                    </h2>
                    <p className="text-foreground-500 text-sm mt-2">
                      千万读者的共同选择
                    </p>
                  </div>
                  <Link
                    to="/category/wuxia"
                    className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
                  >
                    查看更多 <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5" data-product-shop="">
                  {allNovels.slice(0, 8).map((novel) => (
                    <NovelCard key={novel.id} {...novel} />
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Reviews */}
          <section className="py-14 md:py-20 bg-background-50">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-4xl mx-auto">
                <div className="text-center mb-10">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                    读者热评
                  </h2>
                  <p className="text-foreground-500 text-sm mt-3">
                    {novelInfo.ratingCount.toLocaleString()} 位读者的真实评价
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {reviews.map((review, idx) => (
                    <div
                      key={idx}
                      className="p-5 rounded-lg bg-background-100 border border-background-200/70"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
                          <i className="ri-user-line text-lg"></i>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-800">
                            {review.user}
                          </p>
                          <div className="flex items-center gap-0.5 mt-0.5">
                            {Array.from({ length: review.rating }).map((_, i) => (
                              <i key={i} className="ri-star-fill text-accent-500 text-xs"></i>
                            ))}
                          </div>
                        </div>
                        <span className="ml-auto text-xs text-foreground-400">{review.date}</span>
                      </div>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {review.content}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-14 md:py-20 bg-background-100">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-3xl mx-auto">
                <FAQPageSchema questions={faqList} />
                <div className="text-center mb-10">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                    常见问题
                  </h2>
                  <p className="text-foreground-500 text-sm mt-3">
                    关于《二枪入一洞》的热门问答
                  </p>
                </div>
                <div className="space-y-3">
                  {faqList.map((faq, idx) => (
                    <details
                      key={idx}
                      className="group rounded-lg bg-background-50 border border-background-200/70 overflow-hidden"
                    >
                      <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                        <span className="text-sm font-medium text-foreground-800 pr-4">
                          {faq.question}
                        </span>
                        <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform shrink-0"></i>
                      </summary>
                      <div className="px-5 pb-5">
                        <p className="text-sm text-foreground-600 leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    </details>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="py-14 md:py-20 relative overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Ancient%20Chinese%20library%20interior%20with%20wooden%20bookshelves%20and%20scrolls%2C%20warm%20candlelight%20atmosphere%2C%20ink%20wash%20painting%20style%2C%20scholarly%20and%20peaceful%20aesthetic%2C%20soft%20golden%20tones&width=1800&height=600&seq=cta-home&orientation=landscape"
              alt="开始阅读"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-black/50"></div>
            <div className="relative z-10 w-full px-4 md:px-6 text-center">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-background-50 mb-4">
                准备好踏上一段传奇之旅了吗？
              </h2>
              <p className="text-foreground-200 text-sm md:text-base mb-8 max-w-xl mx-auto">
                加入 {novelInfo.favorites.toLocaleString()} 位读者的行列，一起探索这个波澜壮阔的江湖世界。
              </p>
              <Link
                to="/article/1"
                className="inline-flex items-center px-8 py-3 text-base font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-all whitespace-nowrap"
              >
                <i className="ri-play-circle-line mr-2"></i>立即开始阅读
              </Link>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}